import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangleIcon, TagsIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Categorie } from '../CommunicationsConfigurer';
interface CategoryFormDialogProps {
  open: boolean;
  /** Category being edited, or null when creating one. */
  category: Categorie | null;
  onClose: () => void;
  onSave: (category: Categorie) => void;
}
const EMPTY: Omit<Categorie, 'id'> = {
  titre: {
    fr: '',
    en: ''
  },
  description: {
    fr: '',
    en: ''
  },
  urgence: false,
  defaut: false,
  statut: 'active'
};

/** Modal form to create or edit a notice category, in both languages. */
export function CategoryFormDialog({
  open,
  category,
  onClose,
  onSave
}: CategoryFormDialogProps) {
  const {
    t
  } = useLanguage();
  const [draft, setDraft] = useState<Omit<Categorie, 'id'>>(EMPTY);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    setDraft(category ? {
      ...category
    } : EMPTY);
    const id = window.setTimeout(() => firstRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, category, onClose]);
  const canSave = Boolean(draft.titre.fr.trim());
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    onSave({
      ...draft,
      id: category?.id ?? `cat-${Date.now()}`,
      titre: {
        fr: draft.titre.fr.trim(),
        en: draft.titre.en.trim() || draft.titre.fr.trim()
      },
      description: {
        fr: draft.description.fr.trim(),
        en: draft.description.en.trim() || draft.description.fr.trim()
      }
    });
  };
  const inputClass = 'block w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]';
  const statuts: {
    id: Categorie['statut'];
    label: string;
  }[] = [{
    id: 'active',
    label: t('Active', 'Active')
  }, {
    id: 'brouillon',
    label: t('Brouillon', 'Draft')
  }, {
    id: 'inactive',
    label: t('Inactive', 'Inactive')
  }];
  return <AnimatePresence data-id="element-3854" data-mp="1qexcqw3enisi">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} data-id="element-3855" data-mp="1qbt1xfvfposv">
          <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" data-id="element-3856" data-mp="2543ccl65nb0x" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="category-form-title" className="relative w-full max-w-lg bg-white rounded-xl border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto" initial={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} data-id="element-3857" data-mp="2a3xykbezjcfn">
            <form onSubmit={submit} data-id="element-3858" data-mp="3um0g589t5jlg">
              <div className="flex items-start justify-between gap-4 px-6 pt-5" data-id="element-3859" data-mp="3pluk7fwi9axk">
                <div className="flex items-center gap-3" data-id="element-3860" data-mp="q5pizkrngb54">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-indigo-50 text-[#4F46E5]" data-id="element-3861" data-mp="q55zw0q9jgpi">
                    <TagsIcon className="w-4 h-4" data-id="element-3862" data-mp="hdnmtyom5h0n" />
                  </span>
                  <h2 id="category-form-title" className="text-base font-semibold text-[#0F172A]" data-id="element-3863" data-mp="hbt32np2vuy5">
                    {category ? t('Modifier la catégorie', 'Edit category') : t('Nouvelle catégorie', 'New category')}
                  </h2>
                </div>
                <button type="button" onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-3864" data-mp="omnwmx93ks4v">
                  <XIcon className="w-4 h-4" data-id="element-3865" data-mp="klc5uoehm0v3" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5" data-id="element-3866" data-mp="18b6ib4uw9net">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3867" data-mp="18dsz7v3odf9p">
                  <div data-id="element-3868" data-mp="1cc05fw0iiy6n">
                    <label htmlFor="cat-title-fr" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3869" data-mp="1cfe6n0xfx1ci">
                      {t('Titre (français)', 'Title (French)')}
                    </label>
                    <input ref={firstRef} id="cat-title-fr" value={draft.titre.fr} onChange={e => setDraft(d => ({
                  ...d,
                  titre: {
                    ...d.titre,
                    fr: e.target.value
                  }
                }))} placeholder={t('Ex. : Travaux', 'E.g. Travaux')} className={inputClass} data-id="element-3870" data-mp="1r9shge6up4g3" />
                  </div>
                  <div data-id="element-3871" data-mp="2a95gmsxrlelh">
                    <label htmlFor="cat-title-en" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3872" data-mp="1mgov28etj4hf">
                      {t('Titre (anglais)', 'Title (English)')}
                    </label>
                    <input id="cat-title-en" value={draft.titre.en} onChange={e => setDraft(d => ({
                  ...d,
                  titre: {
                    ...d.titre,
                    en: e.target.value
                  }
                }))} placeholder={t('Ex. : Public works', 'E.g. Public works')} className={inputClass} data-id="element-3873" data-mp="1wd1od56gbgbn" />
                  </div>
                  <div data-id="element-3874" data-mp="3jx42amktzysj">
                    <label htmlFor="cat-desc-fr" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3875" data-mp="3jwkwg48gh0uv">
                      {t('Description (français)', 'Description (French)')}
                    </label>
                    <input id="cat-desc-fr" value={draft.description.fr} onChange={e => setDraft(d => ({
                  ...d,
                  description: {
                    ...d.description,
                    fr: e.target.value
                  }
                }))} className={inputClass} data-id="element-3876" data-mp="2w7097959rhfs" />
                  </div>
                  <div data-id="element-3877" data-mp="2dexqztczbt3f">
                    <label htmlFor="cat-desc-en" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3878" data-mp="2defmk9g5y8kf">
                      {t('Description (anglais)', 'Description (English)')}
                    </label>
                    <input id="cat-desc-en" value={draft.description.en} onChange={e => setDraft(d => ({
                  ...d,
                  description: {
                    ...d.description,
                    en: e.target.value
                  }
                }))} className={inputClass} data-id="element-3879" data-mp="1vx8r9zd9sp5x" />
                  </div>
                </div>

                <div className="space-y-3" data-id="element-3880" data-mp="18atsqiesw5m1">
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-id="element-3881" data-mp="13aeiw0tq9g8t">
                    <input type="checkbox" checked={draft.urgence} onChange={e => setDraft(d => ({
                  ...d,
                  urgence: e.target.checked
                }))} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-3882" data-mp="z8klq2iqqude" />
                    <span data-id="element-3883" data-mp="2i5wb9w4djv5m">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900" data-id="element-3884" data-mp="2i54ewj2ydtea">
                        <AlertTriangleIcon className="w-3.5 h-3.5 text-red-500" data-id="element-3885" data-mp="24e93p04zf07j" />
                        {t('Catégorie d’urgence', 'Emergency category')}
                      </span>
                      <span className="block text-xs text-slate-500 mt-0.5" data-id="element-3886" data-mp="1uhmlkj41in97">
                        {t('Signalée en rouge et diffusée en priorité.', 'Flagged in red and delivered with priority.')}
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-id="element-3887" data-mp="1pjkzdpbbi7ti">
                    <input type="checkbox" checked={draft.defaut} onChange={e => setDraft(d => ({
                  ...d,
                  defaut: e.target.checked
                }))} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-3888" data-mp="11v4xoslqi5y0" />
                    <span data-id="element-3889" data-mp="1kth6gd8qhdt2">
                      <span className="block text-sm font-medium text-slate-900" data-id="element-3890" data-mp="1ks66koiesq78">
                        {t('Catégorie par défaut', 'Default category')}
                      </span>
                      <span className="block text-xs text-slate-500 mt-0.5" data-id="element-3891" data-mp="1uiipydehmdbs">
                        {t('Présélectionnée à la création d’un avis. Une seule catégorie peut l’être.', 'Preselected when creating a notice. Only one category can be.')}
                      </span>
                    </span>
                  </label>
                </div>

                <div data-id="element-3892" data-mp="2khsrh01t4f5e">
                  <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3893" data-mp="2kghmiwwml1cp">
                    {t('Statut', 'Status')}
                  </span>
                  <div role="radiogroup" aria-label={t('Statut', 'Status')} className="inline-flex p-1 bg-slate-100 rounded-lg" data-id="element-3894" data-mp="2bip7zuyq9xqd">
                    {statuts.map(s => <button key={s.id} type="button" role="radio" aria-checked={draft.statut === s.id} onClick={() => setDraft(d => ({
                  ...d,
                  statut: s.id
                }))} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${draft.statut === s.id ? 'bg-white text-[#0F172A] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-3895" data-mp="344qpm1zpirqz">
                        {s.label}
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100" data-id="element-3896" data-mp="1nezkiqb3yuu7">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-3897" data-mp="1rezzqd617a2b">
                  {t('Annuler', 'Cancel')}
                </button>
                <button type="submit" disabled={!canSave} className="px-4 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3898" data-mp="26azf88vh0chh">
                  {category ? t('Enregistrer les modifications', 'Save changes') : t('Créer la catégorie', 'Create category')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}